import { useState } from "react";
import { Sparkles, TrendingUp, AlertTriangle, CheckCircle2, ArrowRight, Copy, Check, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

export type InsightTone = "positive" | "watch" | "opportunity";

export interface InsightAction {
  label: string;
  to: string;
}

export interface Insight {
  tone: InsightTone;
  title: string;
  body: string;
  /** Optional deep-links to a persona view, filter, or external resource. */
  actions?: InsightAction[];
}

const toneMap: Record<InsightTone, { icon: typeof Sparkles; ring: string; text: string; bg: string; label: string }> = {
  positive:    { icon: CheckCircle2,  ring: "ring-emerald-500/20", text: "text-emerald-600",  bg: "bg-emerald-500/10",  label: "Working well" },
  watch:       { icon: AlertTriangle, ring: "ring-amber-500/20",   text: "text-amber-600",    bg: "bg-amber-500/10",    label: "Watch closely" },
  opportunity: { icon: TrendingUp,    ring: "ring-primary/20",     text: "text-primary",      bg: "bg-primary/10",      label: "Opportunity" },
};

interface Props {
  title?: string;
  subtitle?: string;
  insights: Insight[];
  columns?: 1 | 2 | 3;
  /** Optional pill shown next to the section title when insights are filter-scoped. */
  contextLabel?: string | null;
  /** Show skeleton cards instead of content (for streaming/generation states). */
  loading?: boolean;
  /** Rendered when insights is empty and not loading. */
  emptyMessage?: string;
  /** Show a "Copy insight" button on each card. Defaults to true. */
  enableCopy?: boolean;
  /** When set, shows a "Send to parent" button that opens a prefilled mailto: draft. */
  sendToParent?: {
    to?: string;
    subject?: string;
    /** Prefixed to the email body (e.g. "Hi Anika's family,"). */
    intro?: string;
    /** Appended after the insight (e.g. signature or next steps link). */
    signature?: string;
  };
}

export default function AiInsights({
  title = "AI Institutional Insights",
  subtitle,
  insights,
  columns = 2,
  contextLabel,
  loading = false,
  emptyMessage = "No insights match the current filters. Reset to see school-wide signals.",
  enableCopy = true,
  sendToParent,
}: Props) {
  const cols = columns === 1 ? "md:grid-cols-1" : columns === 3 ? "md:grid-cols-3" : "md:grid-cols-2";
  const isEmpty = !loading && insights.length === 0;
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  const formatInsight = (it: Insight) => {
    const actions = it.actions?.length ? `\n\nNext steps: ${it.actions.map((a) => a.label).join(" · ")}` : "";
    return `${it.title}\n\n${it.body}${actions}`;
  };

  const handleCopy = async (it: Insight, i: number) => {
    try {
      await navigator.clipboard.writeText(formatInsight(it));
      setCopiedIdx(i);
      toast({ title: "Insight copied", description: "Paste into notes, chat, or your parent update." });
      setTimeout(() => setCopiedIdx((v) => (v === i ? null : v)), 1600);
    } catch {
      toast({ title: "Copy failed", description: "Your browser blocked clipboard access.", variant: "destructive" });
    }
  };

  const handleSendToParent = (it: Insight) => {
    if (!sendToParent) return;
    const subject = encodeURIComponent(sendToParent.subject ?? `WeKIT insight — ${it.title}`);
    const parts = [sendToParent.intro, formatInsight(it), sendToParent.signature].filter(Boolean);
    const body = encodeURIComponent(parts.join("\n\n"));
    const to = encodeURIComponent(sendToParent.to ?? "");
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  };


  return (
    <section className="space-y-4" aria-label={title} aria-busy={loading}>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary grid place-items-center">
          <Sparkles className="w-4 h-4" aria-hidden="true" />
        </div>
        <div>
          <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        {contextLabel && (
          <span
            className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-2.5 py-1 text-[11px] font-medium ring-1 ring-primary/20"
            aria-label={`Insights scoped to ${contextLabel}`}
          >
            <Sparkles className="w-3 h-3" aria-hidden="true" />
            Adjusted for: {contextLabel}
          </span>
        )}
      </div>

      {loading && (
        <div className={cn("grid gap-4", cols)} aria-hidden="true">
          {Array.from({ length: columns === 1 ? 2 : columns * 2 }).map((_, i) => (
            <Card key={i} className="border-0 ring-1 ring-border/50">
              <CardHeader className="pb-2 space-y-2">
                <Skeleton className="h-8 w-8 rounded-lg" />
                <Skeleton className="h-4 w-2/3" />
              </CardHeader>
              <CardContent className="space-y-2">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-5/6" />
                <Skeleton className="h-3 w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {isEmpty && (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            <Sparkles className="w-5 h-5 mx-auto mb-2 opacity-60" aria-hidden="true" />
            {emptyMessage}
          </CardContent>
        </Card>
      )}

      {!loading && !isEmpty && (
        <div className={cn("grid gap-4", cols)}>
          {insights.map((it, i) => {
            const t = toneMap[it.tone];
            const Icon = t.icon;
            return (
              <Card key={i} className={cn("border-0 ring-1", t.ring, "shadow-sm hover:shadow-md transition-shadow")}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <div className={cn("w-8 h-8 rounded-lg grid place-items-center", t.bg, t.text)}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className={cn("text-[10px] uppercase tracking-wider font-semibold", t.text)}>{t.label}</span>
                  </div>
                  <CardTitle className="text-base pt-2 leading-snug">{it.title}</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground leading-relaxed space-y-3">
                  <p>{it.body}</p>
                  {it.actions && it.actions.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {it.actions.map((a, j) => (
                        <Link
                          key={j}
                          to={a.to}
                          className={cn(
                            "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 transition-colors",
                            t.bg, t.text, t.ring,
                            "hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-offset-background"
                          )}
                        >
                          {a.label}
                          <ArrowRight className="w-3 h-3" aria-hidden="true" />
                        </Link>
                      ))}
                    </div>
                  )}
                  {(enableCopy || sendToParent) && (
                    <div className="flex flex-wrap gap-1.5 pt-2 border-t border-border/40 -mx-1 px-1">
                      {enableCopy && (
                        <button
                          type="button"
                          onClick={() => handleCopy(it, i)}
                          aria-label={`Copy insight: ${it.title}`}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {copiedIdx === i ? (
                            <><Check className="w-3 h-3 text-emerald-600" aria-hidden="true" /> Copied</>
                          ) : (
                            <><Copy className="w-3 h-3" aria-hidden="true" /> Copy insight</>
                          )}
                        </button>
                      )}
                      {sendToParent && (
                        <button
                          type="button"
                          onClick={() => handleSendToParent(it)}
                          aria-label={`Send insight to parent: ${it.title}`}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <Mail className="w-3 h-3" aria-hidden="true" /> Send to parent
                        </button>
                      )}
                    </div>
                  )}
                </CardContent>

              </Card>
            );
          })}
        </div>
      )}
    </section>
  );
}
